import axios, { AxiosRequestConfig } from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.glauncher.kz';

const api = axios.create({
  baseURL: API_BASE_URL,
  // Send httpOnly refresh cookie on every cross-origin request
  withCredentials: true,
});

// ── Token helpers ──────────────────────────────────────────────────────────
export const getAccessToken = () =>
  localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

let authGeneration = 0;
type AuthRequest = AxiosRequestConfig & { _retry?: boolean; _authGeneration?: number; _authToken?: string | null };
type RefreshCycle = { generation: number; token: string | null;
  queue: Array<{ resolve: (token: string) => void; reject: (error: unknown) => void }> };
let activeRefresh: RefreshCycle | null = null;
let lastRotation: { generation: number; previous: string; current: string } | null = null;
const sessionChanged = () => Object.assign(new Error('Authentication session changed'), { code: 'AUTH_SESSION_CHANGED' });
const sameSession = (cycle: RefreshCycle) => cycle.generation === authGeneration && cycle.token === getAccessToken();
const tokenSubject = (token: string): string | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch { return null; }
};
const rejectQueue = (cycle: RefreshCycle, error: unknown) => {
  cycle.queue.splice(0).forEach(waiter => waiter.reject(error));
};

export const setAccessToken = (token: string) => {
  // Mirror the original storage location (prefer localStorage, fallback sessionStorage)
  if (localStorage.getItem('access_token') !== null) {
    localStorage.setItem('access_token', token);
  } else {
    sessionStorage.setItem('access_token', token);
  }
};

export const clearTokens = () => {
  authGeneration++;
  lastRotation = null;
  localStorage.removeItem('access_token');
  sessionStorage.removeItem('access_token');
  if (activeRefresh) { rejectQueue(activeRefresh, sessionChanged()); activeRefresh = null; }
};

// ── Request interceptor: attach Bearer token ───────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    const origin = config as AuthRequest;
    // Axios runs request interceptors asynchronously, including replays. A
    // different login can arrive between refresh completion and this dispatch.
    if (origin._authGeneration !== undefined) {
      const knownRotation = lastRotation?.generation === authGeneration
        && origin._authToken === lastRotation.previous && token === lastRotation.current;
      if (origin._authGeneration !== authGeneration || (origin._authToken !== token && !knownRotation)) {
        return Promise.reject(sessionChanged());
      }
    } else {
      origin._authGeneration = authGeneration;
      origin._authToken = token;
    }
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: 401 → try silent refresh → replay ───────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as AuthRequest | undefined;

    // Only intercept 401 errors on non-retry, non-refresh requests
    if (
      !original || error.response?.status !== 401 ||
      original._retry ||
      original.url === '/auth/refresh'
    ) {
      return Promise.reject(error);
    }

    const token = getAccessToken();
    // Requests sent immediately before our successful rotation can receive
    // their 401 later. Replay once with that same session's known new token.
    if (lastRotation && lastRotation.generation === authGeneration
        && original._authGeneration === authGeneration && original._authToken === lastRotation.previous
        && token === lastRotation.current) {
      original._retry = true;
      original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
      return api(original);
    }
    if (!token || (original._authGeneration !== undefined && original._authGeneration !== authGeneration)
        || (original._authToken !== undefined && original._authToken !== token)) {
      return Promise.reject(sessionChanged());
    }
    if (activeRefresh && sameSession(activeRefresh)) {
      const cycle = activeRefresh;
      // Another refresh is in-flight — queue this request
      return new Promise<string>((resolve, reject) => {
        cycle.queue.push({ resolve, reject });
      }).then((newToken) => {
        if (authGeneration !== cycle.generation || getAccessToken() !== newToken) throw sessionChanged();
        original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
        original._retry = true;
        return api(original);
      });
    }

    original._retry = true;
    const cycle: RefreshCycle = { generation: authGeneration, token, queue: [] };
    activeRefresh = cycle;

    try {
      // POST /auth/refresh — no body needed; refresh cookie sent via withCredentials
      const { data } = await axios.post<{ access_token: string }>(
        `${API_BASE_URL}/auth/refresh`,
        null,
        { withCredentials: true }
      );
      const newToken = data.access_token;
      if (!sameSession(cycle)) throw sessionChanged();
      if (typeof newToken !== 'string' || !newToken) throw new Error('Invalid refresh response');
      const previousSubject = tokenSubject(token), refreshedSubject = tokenSubject(newToken);
      // Tabs can hold different sessionStorage access tokens but share the
      // refresh cookie. Never replay a paid operation as the cookie's other user.
      if (previousSubject && refreshedSubject && previousSubject !== refreshedSubject) {
        clearTokens();
        throw sessionChanged();
      }
      setAccessToken(newToken);
      lastRotation = { generation: cycle.generation, previous: token, current: newToken };
      cycle.queue.splice(0).forEach(waiter => waiter.resolve(newToken));
      original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
      return api(original);
    } catch (refreshError: unknown) {
      rejectQueue(cycle, refreshError);
      const status = (refreshError as { response?: { status?: number } })?.response?.status;
      if (sameSession(cycle) && (status === 401 || status === 403)) {
        clearTokens();
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      if (activeRefresh === cycle) activeRefresh = null;
    }
  }
);

// ── Logout helper (call POST /auth/logout then clear local state) ──────────
export const logout = async () => {
  clearTokens();
  try {
    await axios.post(
      `${API_BASE_URL}/auth/logout`,
      null,
      { withCredentials: true }
    );
  } catch {
    // Ignore — proceed with local cleanup regardless
  }
};

export default api;
